// src/services/beverageEnhancementService.js
/**
 * Beverage Enhancement Service
 * Handles AI enhancement of beverage data using the comprehensive models
 */

import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase-config";
import aiService from "./aiService.js";
import {
  baseBeverageSchema,
  aiEnhancementSchema,
} from "../models/beverageModels";

export class BeverageEnhancementService {
  constructor() {
    this.menuItemsCollection = "menu_items";
    this.enhancementsCollection = "beverage_enhancements";
  }

  /**
   * Enhance a beverage with AI-generated data
   * @param {string} restaurantId - Restaurant ID
   * @param {string} beverageId - Beverage ID to enhance
   * @param {object} options - Enhancement options
   */
  async enhanceBeverage(restaurantId, beverageId, options = {}) {
    try {
      console.log(`🤖 Starting AI enhancement for beverage: ${beverageId}`);

      // Get current beverage data
      const beverageRef = doc(db, this.menuItemsCollection, beverageId);
      const beverageSnap = await getDoc(beverageRef);

      if (!beverageSnap.exists()) {
        throw new Error(`Beverage ${beverageId} not found`);
      }

      const beverageData = beverageSnap.data();

      // Check if already enhanced and not forcing re-enhancement
      if (beverageData.aiEnhanced && !options.forceReenhancement) {
        console.log(`📝 Beverage ${beverageId} already enhanced`);
        return beverageData;
      }

      // Determine beverage category and enhance accordingly
      const category =
        beverageData.category ||
        this.detectCategory(beverageData.name, beverageData.description);

      let enhancementData = {};

      switch (category.toLowerCase()) {
        case "wine":
          enhancementData = await this.enhanceWine(beverageData);
          break;
        case "beer":
          enhancementData = await this.enhanceBeer(beverageData);
          break;
        case "spirit":
        case "spirits":
          enhancementData = await this.enhanceSpirit(beverageData);
          break;
        case "cocktail":
          enhancementData = await this.enhanceCocktail(beverageData);
          break;
        default:
          enhancementData = await this.enhanceGenericBeverage(beverageData);
      }

      // Merge enhancement data with original beverage
      const enhancedBeverage = {
        ...beverageData,
        category: category,
        aiEnhanced: true,
        aiEnhancedAt: new Date(),
        aiVersion: "1.0",
        enhancement: enhancementData,
      };

      // Update beverage in database
      await updateDoc(beverageRef, enhancedBeverage);

      // Store detailed enhancement data separately
      await this.storeEnhancementData(beverageId, enhancementData);

      console.log(`✅ Successfully enhanced beverage: ${beverageId}`);
      return enhancedBeverage;
    } catch (error) {
      console.error(`❌ Error enhancing beverage ${beverageId}:`, error);
      throw error;
    }
  }

  /**
   * Enhance wine with AI analysis
   */
  async enhanceWine(beverageData) {
    try {
      const prompt = `Analyze this wine and provide comprehensive information:
      
Name: ${beverageData.name}
Description: ${beverageData.description || "No description provided"}
Price: $${beverageData.price || "Not specified"}

Please provide detailed analysis including:
1. Professional ratings and reviews
2. Detailed tasting notes (aroma, palate, finish)
3. Producer information and history
4. Vintage characteristics (if vintage specified)
5. Food pairing recommendations
6. Investment potential and market data
7. Optimal serving conditions

Format the response as detailed JSON matching the wine enhancement schema.`;

      const aiResponse = await aiService.callAnthropicAI(
        prompt,
        "claude-3-5-sonnet-20241022"
      );
      return this.parseAIResponse(aiResponse, "wine");
    } catch (error) {
      console.error("Error enhancing wine:", error);
      return this.getFallbackEnhancement("wine");
    }
  }

  /**
   * Enhance beer with AI analysis
   */
  async enhanceBeer(beverageData) {
    try {
      const prompt = `Analyze this beer and provide comprehensive information:
      
Name: ${beverageData.name}
Description: ${beverageData.description || "No description provided"}
Price: $${beverageData.price || "Not specified"}

Please provide detailed analysis including:
1. Beer style classification (BJCP style if applicable)
2. Brewing details (ABV, IBU, SRM, hops, malts)
3. Brewery information and history
4. Tasting notes and flavor profile
5. Food pairing recommendations
6. Seasonal availability and occasions
7. Similar beer recommendations

Format the response as detailed JSON matching the beer enhancement schema.`;

      const aiResponse = await aiService.callAnthropicAI(
        prompt,
        "claude-3-5-sonnet-20241022"
      );
      return this.parseAIResponse(aiResponse, "beer");
    } catch (error) {
      console.error("Error enhancing beer:", error);
      return this.getFallbackEnhancement("beer");
    }
  }

  /**
   * Enhance spirit with AI analysis
   */
  async enhanceSpirit(beverageData) {
    try {
      const prompt = `Analyze this spirit and provide comprehensive information:
      
Name: ${beverageData.name}
Description: ${beverageData.description || "No description provided"}
Price: $${beverageData.price || "Not specified"}

Please provide detailed analysis including:
1. Spirit classification and style
2. Distillation and maturation details
3. Distillery information and history
4. Detailed tasting notes and profile
5. Cocktail recommendations using this spirit
6. Food pairing suggestions
7. Investment and collectibility information
8. Serving recommendations

Format the response as detailed JSON matching the spirit enhancement schema.`;

      const aiResponse = await aiService.callAnthropicAI(
        prompt,
        "claude-3-5-sonnet-20241022"
      );
      return this.parseAIResponse(aiResponse, "spirit");
    } catch (error) {
      console.error("Error enhancing spirit:", error);
      return this.getFallbackEnhancement("spirit");
    }
  }

  /**
   * Enhance cocktail with AI analysis
   */
  async enhanceCocktail(beverageData) {
    try {
      const prompt = `Analyze this cocktail and provide comprehensive information:
      
Name: ${beverageData.name}
Description: ${beverageData.description || "No description provided"}
Price: $${beverageData.price || "Not specified"}

Please provide detailed analysis including:
1. Cocktail style and classification
2. Base spirit and ingredient breakdown
3. Preparation technique and glassware
4. Flavor profile and tasting notes
5. History and origin story
6. Variations and similar cocktails
7. Food pairing recommendations
8. Occasion and time recommendations

Format the response as detailed JSON matching the cocktail enhancement schema.`;

      const aiResponse = await aiService.callAnthropicAI(
        prompt,
        "claude-3-5-sonnet-20241022"
      );
      return this.parseAIResponse(aiResponse, "cocktail");
    } catch (error) {
      console.error("Error enhancing cocktail:", error);
      return this.getFallbackEnhancement("cocktail");
    }
  }

  /**
   * Generic beverage enhancement for other categories
   */
  async enhanceGenericBeverage(beverageData) {
    try {
      const prompt = `Analyze this beverage and provide comprehensive information:
      
Name: ${beverageData.name}
Description: ${beverageData.description || "No description provided"}
Category: ${beverageData.category || "Unknown"}
Price: $${beverageData.price || "Not specified"}

Please provide detailed analysis including:
1. Beverage classification and style
2. Producer/brand information
3. Flavor profile and characteristics
4. Serving suggestions
5. Food pairing recommendations
6. Occasions and seasonality
7. Similar beverage recommendations

Format the response as detailed JSON.`;

      const aiResponse = await aiService.callAnthropicAI(
        prompt,
        "claude-3-5-sonnet-20241022"
      );
      return this.parseAIResponse(aiResponse, "generic");
    } catch (error) {
      console.error("Error enhancing generic beverage:", error);
      return this.getFallbackEnhancement("generic");
    }
  }

  /**
   * Parse AI response and structure according to schema
   */
  parseAIResponse(aiResponse, category) {
    try {
      // Try to parse JSON response
      const parsed = JSON.parse(aiResponse);

      // Validate and structure according to schema
      return this.validateAndStructureData(parsed, category);
    } catch (error) {
      console.error("Error parsing AI response:", error);

      // Fallback: extract key information from text response
      return this.extractDataFromText(aiResponse, category);
    }
  }

  /**
   * Validate and structure enhancement data according to schema
   */
  validateAndStructureData(data, category) {
    const structured = {
      ...aiEnhancementSchema,
      categoryDetails: {
        [category]: data.categoryDetails?.[category] || {},
      },
    };

    // Apply category-specific validation and defaults
    switch (category) {
      case "wine":
        structured.categoryDetails.wine = {
          vintage: data.vintage || null,
          varietal: Array.isArray(data.varietal) ? data.varietal : [],
          blend: data.blend || "",
          vineyard: data.vineyard || "",
          ageability: data.ageability || {},
          winemaking: data.winemaking || {},
          terroir: data.terroir || {},
        };
        break;

      case "beer":
        structured.categoryDetails.beer = {
          style: data.style || "",
          bjcpStyle: data.bjcpStyle || "",
          abv: data.abv || 0,
          ibu: data.ibu || 0,
          srm: data.srm || 0,
          hops: Array.isArray(data.hops) ? data.hops : [],
          malts: Array.isArray(data.malts) ? data.malts : [],
          yeast: data.yeast || "",
          brewing: data.brewing || {},
          seasonality: data.seasonality || "",
        };
        break;

      case "spirit":
        structured.categoryDetails.spirit = {
          type: data.type || "",
          subtype: data.subtype || "",
          age: data.age || "",
          proof: data.proof || 0,
          abv: data.abv || 0,
          distillation: data.distillation || {},
          maturation: data.maturation || {},
          collectibility: data.collectibility || {},
        };
        break;

      case "cocktail":
        structured.categoryDetails.cocktail = {
          baseSpirit: data.baseSpirit || "",
          style: data.style || "",
          glassware: data.glassware || "",
          garnish: data.garnish || "",
          difficulty: data.difficulty || "",
          ingredients: Array.isArray(data.ingredients) ? data.ingredients : [],
          technique: data.technique || "",
          origin: data.origin || "",
          variations: Array.isArray(data.variations) ? data.variations : [],
        };
        break;
    }

    // Common fields
    structured.ratings = Array.isArray(data.ratings) ? data.ratings : [];
    structured.tastingNotes = data.tastingNotes || {};
    structured.producer = data.producer || {};
    structured.pairings = data.pairings || {};
    structured.market = data.market || {};

    return structured;
  }

  /**
   * Extract data from text response when JSON parsing fails
   */
  extractDataFromText(textResponse, category) {
    // Simple text extraction logic
    const enhancement = { ...aiEnhancementSchema };

    // Extract basic information using regex patterns
    const lines = textResponse.split("\n");

    lines.forEach((line) => {
      // Extract ratings
      if (line.includes("rating") || line.includes("score")) {
        // Parse rating information
      }

      // Extract tasting notes
      if (line.includes("aroma") || line.includes("nose")) {
        // Parse aroma information
      }

      // Extract pairings
      if (line.includes("pair") || line.includes("food")) {
        // Parse pairing information
      }
    });

    enhancement.aiMetadata = {
      confidence: 0.7, // Lower confidence for text extraction
      sources: ["AI Analysis"],
      lastUpdated: new Date(),
      version: "1.0",
      processingNotes: "Extracted from text response",
    };

    return enhancement;
  }

  /**
   * Get fallback enhancement data when AI fails
   */
  getFallbackEnhancement(category) {
    return {
      ...aiEnhancementSchema,
      categoryDetails: {
        [category]: {},
      },
      aiMetadata: {
        confidence: 0.3,
        sources: ["Fallback Data"],
        lastUpdated: new Date(),
        version: "1.0",
        processingNotes: "AI enhancement failed, using fallback data",
      },
    };
  }

  /**
   * Store detailed enhancement data separately
   */
  async storeEnhancementData(beverageId, enhancementData) {
    try {
      const enhancementRef = doc(db, this.enhancementsCollection, beverageId);
      await setDoc(enhancementRef, {
        beverageId,
        enhancementData,
        createdAt: new Date(),
        version: "1.0",
      });
    } catch (error) {
      console.error("Error storing enhancement data:", error);
      // Non-critical error, don't throw
    }
  }

  /**
   * Detect beverage category from name and description
   */
  detectCategory(name, description = "") {
    const text = `${name} ${description}`.toLowerCase();

    // Wine keywords
    if (
      text.match(
        /(wine|chardonnay|cabernet|merlot|pinot|sauvignon|riesling|bordeaux|burgundy|vintage)/
      )
    ) {
      return "wine";
    }

    // Beer keywords
    if (
      text.match(/(beer|ale|lager|ipa|stout|porter|pilsner|wheat|brewery|hop)/)
    ) {
      return "beer";
    }

    // Spirit keywords
    if (
      text.match(
        /(whiskey|bourbon|scotch|vodka|gin|rum|tequila|brandy|cognac|distillery)/
      )
    ) {
      return "spirit";
    }

    // Cocktail keywords
    if (
      text.match(
        /(cocktail|martini|manhattan|negroni|margarita|mojito|old fashioned|mixed drink)/
      )
    ) {
      return "cocktail";
    }

    return "unknown";
  }

  /**
   * Batch enhance multiple beverages
   */
  async batchEnhanceBeverages(restaurantId, beverageIds, options = {}) {
    const results = [];

    for (const beverageId of beverageIds) {
      try {
        const result = await this.enhanceBeverage(
          restaurantId,
          beverageId,
          options
        );
        results.push({ beverageId, success: true, data: result });

        // Add delay to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        results.push({ beverageId, success: false, error: error.message });
      }
    }

    return results;
  }

  /**
   * Get enhancement status for a restaurant's beverages
   */
  async getEnhancementStatus(restaurantId) {
    try {
      const q = query(
        collection(db, this.menuItemsCollection),
        where("restaurantId", "==", restaurantId)
      );

      const snapshot = await getDocs(q);
      const stats = {
        total: 0,
        enhanced: 0,
        pending: 0,
        categories: {},
      };

      snapshot.forEach((doc) => {
        const data = doc.data();
        stats.total++;

        if (data.aiEnhanced) {
          stats.enhanced++;
        } else {
          stats.pending++;
        }

        const category = data.category || "unknown";
        if (!stats.categories[category]) {
          stats.categories[category] = { total: 0, enhanced: 0 };
        }
        stats.categories[category].total++;
        if (data.aiEnhanced) {
          stats.categories[category].enhanced++;
        }
      });

      return stats;
    } catch (error) {
      console.error("Error getting enhancement status:", error);
      throw error;
    }
  }

  /**
   * PHASE 2 INTEGRATION METHODS
   * Enhanced methods for Phase 2 AI integration
   */

  /**
   * Enhance beverage using Phase 2 pipeline
   */
  async enhanceBeverageWithPhase2(restaurantId, beverageId, options = {}) {
    try {
      // Import Phase 2 services dynamically to avoid circular dependencies
      const { masterOrchestrator } = await import(
        "./masterServiceOrchestrator.js"
      );

      const beverageRef = doc(db, this.menuItemsCollection, beverageId);
      const beverageSnap = await getDoc(beverageRef);

      if (!beverageSnap.exists()) {
        throw new Error(`Beverage ${beverageId} not found`);
      }

      const beverageData = beverageSnap.data();

      // Use master orchestrator for Phase 2 enhancement
      const enhancedData = await masterOrchestrator.enhanceBeverage(
        beverageData,
        {
          ...options,
          restaurantId,
          phase2Features: true,
        }
      );

      // Update the beverage with enhanced data
      await updateDoc(beverageRef, {
        ...enhancedData,
        lastEnhanced: new Date().toISOString(),
        enhancementVersion: "2.0",
        updatedAt: new Date().toISOString(),
      });

      return {
        beverageId,
        enhanced: true,
        version: "2.0",
        features: enhancedData.enhanced,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error in Phase 2 beverage enhancement:", error);
      // Fallback to legacy enhancement
      return this.enhanceBeverage(restaurantId, beverageId, options);
    }
  }

  /**
   * Batch enhance beverages with Phase 2 capabilities
   */
  async batchEnhanceWithPhase2(restaurantId, beverageIds, options = {}) {
    try {
      const results = [];
      const { masterOrchestrator } = await import(
        "./masterServiceOrchestrator.js"
      );

      await masterOrchestrator.initialize();

      for (const beverageId of beverageIds) {
        try {
          const result = await this.enhanceBeverageWithPhase2(
            restaurantId,
            beverageId,
            options
          );
          results.push({ ...result, success: true });
        } catch (error) {
          results.push({
            beverageId,
            success: false,
            error: error.message,
            timestamp: new Date().toISOString(),
          });
        }

        // Add delay to avoid rate limiting
        if (options.delay && options.delay > 0) {
          await new Promise((resolve) => setTimeout(resolve, options.delay));
        }
      }

      return {
        total: beverageIds.length,
        successful: results.filter((r) => r.success).length,
        failed: results.filter((r) => !r.success).length,
        results,
      };
    } catch (error) {
      console.error("Error in batch Phase 2 enhancement:", error);
      throw error;
    }
  }

  /**
   * Get enhancement recommendations for a restaurant's menu
   */
  async getEnhancementRecommendations(restaurantId) {
    try {
      const stats = await this.getEnhancementStatus(restaurantId);
      const recommendations = [];

      // Recommend items that need enhancement
      if (stats.unenhanced > 0) {
        recommendations.push({
          type: "enhance_items",
          priority: "high",
          count: stats.unenhanced,
          description: `${stats.unenhanced} items need AI enhancement`,
          action: "enhance_all",
        });
      }

      // Recommend categories with low enhancement rates
      Object.entries(stats.categories).forEach(([category, data]) => {
        const enhancementRate = data.enhanced / data.total;
        if (enhancementRate < 0.5 && data.total > 2) {
          recommendations.push({
            type: "enhance_category",
            priority: "medium",
            category,
            enhancementRate: Math.round(enhancementRate * 100),
            description: `${category} category has low enhancement rate (${Math.round(
              enhancementRate * 100
            )}%)`,
            action: "enhance_category",
          });
        }
      });

      // Recommend Phase 2 upgrade for legacy enhancements
      const legacyEnhanced = await this.getLegacyEnhancedCount(restaurantId);
      if (legacyEnhanced > 0) {
        recommendations.push({
          type: "upgrade_to_phase2",
          priority: "medium",
          count: legacyEnhanced,
          description: `${legacyEnhanced} items can be upgraded to Phase 2 enhancements`,
          action: "upgrade_phase2",
        });
      }

      // Sort recommendations by priority
      const sortedRecommendations = recommendations.toSorted((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      return {
        total: recommendations.length,
        recommendations: sortedRecommendations,
      };
    } catch (error) {
      console.error("Error getting enhancement recommendations:", error);
      throw error;
    }
  }

  /**
   * Helper method to count legacy enhanced items
   */
  async getLegacyEnhancedCount(restaurantId) {
    try {
      const q = query(
        collection(db, this.menuItemsCollection),
        where("restaurantId", "==", restaurantId),
        where("aiEnhanced", "==", true)
      );

      const querySnapshot = await getDocs(q);
      let legacyCount = 0;

      querySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        // Check if it's legacy enhancement (no version or version < 2.0)
        if (
          !data.enhancementVersion ||
          parseFloat(data.enhancementVersion) < 2.0
        ) {
          legacyCount++;
        }
      });

      return legacyCount;
    } catch (error) {
      console.error("Error counting legacy enhancements:", error);
      return 0;
    }
  }
}

export const beverageEnhancementService = new BeverageEnhancementService();
